import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { produce } from 'immer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setValidationErrors } from '../../../../../../../../common/global/helpers';
import Button from '../../../../../../../../common/widgets/Button';
import Dialog from '../../../../../../../../common/widgets/Dialog';
import Dropdown from '../../../../../../../../common/widgets/Dropdown';
import Input from '../../../../../../../../common/widgets/Input';
import { AddAttendenceTypeData } from '../../../../../../models/AttendenceType';
import { ErrorState, ErrorType } from '../../../../../../models/Errors';
import { PresenceTypeCategoryModel } from '../../../../../../models/TimeAndAttendance';
import { resources } from '../../../../../../resources/resources';
import { timesheetsAdminService } from '../../../../../../services/timesheets-admin';
import {
  Color,
  GetAttendanceTypeCategories,
  GetColors,
  colorItemRender,
  colorValueRender,
} from '../../types';

interface AddNewAttendenceTypeState {
  data: AddAttendenceTypeData;
  selectedColor?: Color;
  selectedCategory?: PresenceTypeCategoryModel;
  isSaving: boolean;
}

interface AddNewAttendenceTypeProps {
  onSaveResponse: (isSaveSuccess: boolean) => void;
  onCloseAddDialog: () => void;
}

export default function AddNewAttendenceType(
  props: Readonly<AddNewAttendenceTypeProps>,
) {
  const { t: translate } = useTranslation();
  const { onSaveResponse, onCloseAddDialog } = props;
  const [state, setState] = useState<AddNewAttendenceTypeState>({
    data: {} as AddAttendenceTypeData,
    selectedCategory: undefined,
    isSaving: false,
  });
  const [errors, setErrors] = useState<ErrorState>({});

  const handleSaveNewAttendanceType = async () => {
    setState(
      produce(d => {
        d.isSaving = true;
      }),
    );
    const response = await timesheetsAdminService.addAttendenceType(state.data);
    if (!response.hasErrors) {
      onSaveResponse(true);
    } else if (response.errorType === ErrorType.ValidationError) {
      setValidationErrors(response, setErrors);
    } else {
      onSaveResponse(false);
    }
    setState(
      produce(d => {
        d.isSaving = false;
      }),
    );
  };

  const handleColorChanged = (e: DropDownListChangeEvent) => {
    setState(
      produce(d => {
        d.selectedColor = e.value;
        d.data.bgColor = e.value.bgColor;
        d.data.fgColor = e.value.fgColor;
      }),
    );
  };

  const handleAttendanceTypeCategoryChanged = (e: DropDownListChangeEvent) => {
    setState(
      produce(d => {
        d.selectedCategory = e.value;
        d.data.categoryId = e.value.categoryId;
      }),
    );
  };

  const clearErrors = (name: string) => {
    setErrors(
      produce(draft => {
        if (draft) {
          draft[name] = [];
        }
      }),
    );
  };

  return (
    <Dialog
      closeDialog={onCloseAddDialog}
      showCloseIcon
      closeOnClickOutside={false}
      className="dialog-m attendence-settings-dialog"
    >
      <div className="dialog-content">
        <div className="dialog-title">
          {translate(resources.admin.timesheetsSettings.dialog.addTitle)}
        </div>
        <Input
          label={translate(
            resources.admin.timesheetsSettings.dialog.nameInEnglish,
          )}
          mandatory
          value={state.data.typeEnDescription}
          onChange={e => {
            setState(
              produce(d => {
                d.data.typeEnDescription = e.value;
              }),
            );
          }}
          errors={errors.typeEnDescription}
          clearErrors={() => clearErrors('typeEnDescription')}
        />

        <Input
          label={translate(
            resources.admin.timesheetsSettings.dialog.nameInRomanian,
          )}
          mandatory
          value={state.data.typeRoDescription}
          onChange={e => {
            setState(
              produce(d => {
                d.data.typeRoDescription = e.value;
              }),
            );
          }}
          errors={errors.typeRoDescription}
          clearErrors={() => clearErrors('typeRoDescription')}
        />
        <Input
          label={translate(
            resources.admin.timesheetsSettings.dialog.shortNotation,
          )}
          mandatory
          value={state.data.shortNotation}
          onChange={e =>
            setState(
              produce(d => {
                d.data.shortNotation = e.value;
              }),
            )
          }
          errors={errors.shortNotation}
          clearErrors={() => clearErrors('shortNotation')}
        />
        <Input
          type="number"
          min={0}
          max={24}
          label={translate(
            resources.admin.timesheetsSettings.dialog.hoursPerDay,
          )}
          value={state.data.hours ?? 0}
          onChange={e =>
            setState(
              produce(d => {
                d.data.hours = Number(e.value);
              }),
            )
          }
          errors={errors.hours}
          clearErrors={() => clearErrors('hours')}
        />
        <Dropdown
          label={translate(
            resources.admin.timesheetsSettings.dialog.pickAColor,
          )}
          mandatory
          data={GetColors()}
          value={state.selectedColor}
          dataItemKey="id"
          textField="colorName"
          valueRender={colorValueRender}
          itemRender={colorItemRender}
          onChange={handleColorChanged}
          errors={errors.bgColor}
          clearErrors={() => clearErrors('bgColor')}
        />

        <Dropdown
          label={translate(resources.admin.timesheetsSettings.dialog.category)}
          mandatory
          data={GetAttendanceTypeCategories()}
          value={state.selectedCategory}
          dataItemKey="categoryId"
          textField="description"
          onChange={handleAttendanceTypeCategoryChanged}
          errors={errors.categoryId}
          clearErrors={() => clearErrors('categoryId')}
        />
      </div>

      <div className="dialog-actions horizontal">
        <Button
          className="btn btn-solid"
          onClick={handleSaveNewAttendanceType}
          loading={state.isSaving}
        >
          {translate(resources.save)}
        </Button>
        <Button onClick={onCloseAddDialog} className="btn btn-outline">
          {translate(resources.cancel)}
        </Button>
      </div>
    </Dialog>
  );
}
